import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight, ChartNoAxesCombined, Code2, ServerCog, ShoppingCart } from "lucide-react";
import { getPage } from "@/content/pages";
import { Asterisk } from "@/components/Asterisk";
import { ContactForm } from "@/components/ContactForm";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import { PageHero } from "@/components/PageHero";
import { MediaImage } from "@/components/MediaImage";
import serviceSeoImage from "@/assets/service-seo.jpg";
import serviceWebDesignImage from "@/assets/service-web-design.jpg";
import serviceEcommerceImage from "@/assets/service-ecommerce.jpg";
import serviceHostingImage from "@/assets/service-hosting.jpg";

const PAGE_KEY = "/services";

const serviceGroups = [
  {
    eyebrow: "SEO strategy",
    title: "SEO strategy & execution",
    body: "I review how customers find your services and what prevents the website from winning their enquiry. Support can start with a focused audit or continue through technical fixes, stronger commercial pages and local SEO. You get a clear plan, direct contact with me and reporting tied to the actions that matter to your business.",
    image: serviceSeoImage,
    alt: "SEO strategy workspace with analytics, keyword notes, and website planning documents",
    Icon: ChartNoAxesCombined,
    to: "/seo",
    cta: "Explore SEO",
    links: [
      { label: "SEO Audits", to: "/seo/bespoke-seo-website-audits" },
      { label: "Organic SEO", to: "/seo/organic-seo-services" },
      { label: "Link Outreach", to: "/seo/link-building-outreach" },
      { label: "Technical SEO", to: "/seo/technical-seo-support" },
    ],
  },
  {
    eyebrow: "Web design",
    title: "Conversion-led web design",
    body: "I plan the content, navigation and layouts around the questions customers need answered before they contact you. The build brings those decisions together in a responsive website with clear service pages and enquiry routes. It can be a new site or a redesign that preserves useful content and existing search visibility.",
    image: serviceWebDesignImage,
    alt: "Web design planning with responsive layouts and conversion-focused website wireframes",
    Icon: Code2,
    to: "/web-design",
    cta: "Explore web design",
    links: [
      { label: "WordPress websites", to: "/wordpress-websites" },
      { label: "Website management", to: "/website-management" },
    ],
  },
  {
    eyebrow: "Ecommerce",
    title: "Shopify ecommerce builds",
    body: "I build Shopify stores around your product range and the way customers browse, compare and buy. The work covers collection structure, product presentation and the agreed payment, delivery and app setup. Test orders and a practical handover help you prepare for launch and manage everyday store updates.",
    image: serviceEcommerceImage,
    alt: "Shopify ecommerce build workspace with product grid, packaging, order notes, and mobile device",
    Icon: ShoppingCart,
    to: "/shopify-websites",
    cta: "Explore Shopify",
    links: [
      { label: "Shopify websites", to: "/shopify-websites" },
      { label: "Ongoing site support", to: "/website-management" },
    ],
  },
  {
    eyebrow: "Hosting & care",
    title: "Hosting and technical care",
    body: "I help keep your website available, maintained and ready for routine business changes. Hosting covers the agreed server environment and support, while website management can include software updates, content work and performance checks. We review the existing setup first and agree which responsibilities your plan needs to cover.",
    image: serviceHostingImage,
    alt: "Server room with illuminated racks providing reliable website hosting",
    Icon: ServerCog,
    to: "/hosting",
    cta: "Explore hosting",
    links: [
      { label: "Hosting", to: "/hosting" },
      { label: "Website management", to: "/website-management" },
    ],
  },
] as const;

export const Route = createFileRoute("/services")({
  loader: () => {
    const page = getPage(PAGE_KEY);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: loaderData.title },
          { name: "description", content: loaderData.description },
          { property: "og:title", content: loaderData.title },
          { property: "og:description", content: loaderData.description },
      { property: "og:url", content: "https://www.phil-carr.co.uk/services" },
        ]
      : [],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/services" }],
  }),
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Page not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: Page,
});

function Page() {
  const page = Route.useLoaderData();
  return (
    <>
      <PageHero title="Services that help you grow" />

      <article className="inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-center">
            <div className="col-lg-7">
              <p className="demo-section-label">Digital marketing & web services</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Choose the support that fits your next <em>move</em>
              </h2>
            </div>
            <div className="col-lg-5">
              <p className="site-copy text-muted-foreground">
                From search visibility to high-converting websites, each service is built around
                clear goals, focused strategy, and practical delivery.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contact" className="demo-link-arrow-button group no-underline">
                  Book a discovery call
                  <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
                <Link to="/about" className="demo-link-arrow-button group no-underline">
                  Learn about Phil
                  <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-16 space-y-14">
            {serviceGroups.map((service, index) => (
              <section key={service.title} className="row g-5 align-items-center">
                <div className={`col-lg-6 ${index % 2 === 1 ? "order-lg-2" : ""}`}>
                  <figure className="demo-image-frame m-0">
                    <MediaImage
                      src={service.image}
                      alt={service.alt}
                      width={1280}
                      height={896}
                    />
                  </figure>
                </div>
                <div className="col-lg-6">
                  <p className="demo-section-label">{service.eyebrow}</p>
                  <h3 className="site-heading-md mt-4 font-display font-light">{service.title}</h3>
                  <p className="site-copy mt-5 text-muted-foreground">{service.body}</p>
                  <ul className="bootstrap-list-row mt-6 row list-none pl-0">
                    {service.links.map((link) => (
                      <li key={link.label} className="col-sm-6 flex items-start gap-3">
                        <Asterisk className="mt-1 h-4 w-4 shrink-0 text-accent" />
                        <Link
                          to={link.to}
                          className="text-foreground underline-offset-4 hover:text-primary hover:underline"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to={service.to} className="demo-link-arrow-button group mt-8 no-underline">
                    {service.cta}
                    <span className="demo-arrow-circle transition-transform group-hover:rotate-45">
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </div>
              </section>
            ))}
          </div>
      </article>

      <RelatedNews count={10} title="Latest news" eyebrow="From the blog" />

      <CtaBand />
      <section className="inner-contact-section border-t border-border bg-background/95">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5">
              <p className="demo-section-label">Get in touch</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Start a <em>conversation</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                Tell me what you need help with and I’ll point you towards the right first step.
              </p>
            </div>
            <div className="col-lg-7">
              <ContactForm pageContext={page.title} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
